'use client';

import React, { useState, useEffect, useMemo } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useLanguage, LANGUAGES } from '@/contexts/LanguageContext';
import type { LangCode } from '@/lib/i18n/lang';
import { marketingLangFromPathname } from '@/lib/i18n/marketing-path';
import { pathWithAffiliateQuery } from '@/lib/marketing/affiliateSource';

/** Mora uključiti sr-cyrl pre sr, inače bi /sr-cyrl/... mogao pogrešno da se parsira. */
function localizedPathForLang(pathname: string, code: LangCode): string | null {
  const m = pathname.match(/^\/(sr-cyrl|sr|en|ru|tr|de)(\/.*)?$/);
  if (!m) return null;
  return `/${code}${m[2] ?? ''}`;
}

export default function PublicNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const pathname = usePathname() ?? '/';
  const router = useRouter();
  const { t, lang, setLang, currentLanguage } = useLanguage();
  const pathLang = marketingLangFromPathname(pathname);
  const homeBase = pathWithAffiliateQuery(`/${pathLang}/home`);

  const pickLang = (code: LangCode) => {
    setLang(code);
    setLangOpen(false);
    const next = localizedPathForLang(pathname, code);
    if (next) {
      const hash = typeof window !== 'undefined' ? window.location.hash : '';
      router.replace(pathWithAffiliateQuery(next, hash));
    }
  };

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const NAV_ITEMS = useMemo(
    () => [
      { labelKey: 'nav.virtualOffice', href: `${homeBase}#virtual-office` },
      { labelKey: 'nav.coworking', href: `${homeBase}#coworking` },
      { labelKey: 'nav.accounting', href: `${homeBase}#accounting` },
      { labelKey: 'nav.blog', href: `/${pathLang}/blog` },
      { labelKey: 'nav.contact', href: `${homeBase}#contact` },
    ],
    [pathLang, homeBase],
  );

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-[200] bg-white/95 backdrop-blur-sm border-b border-slate-200/80 shadow-nav pointer-events-auto isolate">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3 min-h-[7rem] min-w-0">
          {/* Logo only: wordmark is in the asset */}
          <Link href={homeBase} className="flex items-center shrink-0 min-w-0 py-2">
            <AppLogo surface="light" size={48} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS?.map((item) => (
              <a
                key={`nav-${item?.labelKey}`}
                href={item?.href}
                className="nav-link px-4 py-2 rounded-lg hover:bg-slate-50 transition-all duration-150"
              >
                {t(item?.labelKey)}
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all duration-150"
              >
                <Globe size={15} />
                <span>
                  {currentLanguage?.flag}{' '}
                  {currentLanguage?.navAbbrev ?? currentLanguage?.code?.toUpperCase()}
                </span>
                <ChevronDown size={13} className={`transition-transform duration-150 ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-xl border border-slate-200 shadow-card-hover animate-fade-in z-50">
                  {LANGUAGES?.map((l) => (
                    <button
                      key={`lang-${l?.code}`}
                      type="button"
                      onClick={() => pickLang(l?.code)}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left hover:bg-slate-50 transition-colors duration-100 first:rounded-t-xl last:rounded-b-xl ${lang === l?.code ? 'font-semibold text-[#1B3A5C]' : 'text-slate-600'}`}
                    >
                      <span className="text-base">{l?.flag}</span>
                      {l?.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link href="/login" className="btn-secondary text-xs px-4 py-2">
              {t('nav.clientPortal')}
            </Link>
            <a href={`${homeBase}#contact`} className="btn-primary text-xs px-4 py-2">
              {t('nav.getStarted')}
            </a>
          </div>

          {/* Mobile toggle: shrink-0 + touch-manipulation for reliable taps on phones */}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="lg:hidden shrink-0 p-3 -mr-1 rounded-lg text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors duration-150 touch-manipulation"
            aria-expanded={mobileOpen}
            aria-controls="public-mobile-nav"
          >
            {mobileOpen ? <X size={22} strokeWidth={2} /> : <Menu size={22} strokeWidth={2} />}
          </button>
        </div>
      </div>
    </header>

    {/* Mobile menu: fixed layer under header so hero/content never steals taps (iOS / stacking) */}
    {mobileOpen && (
      <>
        <div
          role="presentation"
          className="fixed inset-0 z-[190] bg-slate-900/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
        <nav
          id="public-mobile-nav"
          className="fixed top-[7rem] left-0 right-0 bottom-0 z-[195] lg:hidden bg-white overflow-y-auto overscroll-contain shadow-[0_8px_30px_rgba(0,0,0,0.12)] animate-slide-up touch-manipulation"
          aria-label={t('nav.menu')}
        >
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-1 pb-8">
            {NAV_ITEMS?.map((item) => (
              <a
                key={`mobile-nav-${item?.labelKey}`}
                href={item?.href}
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-3.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors duration-100"
              >
                {t(item?.labelKey)}
              </a>
            ))}
            <div className="pt-2 pb-1 border-t border-slate-100 mt-2">
              <div className="flex flex-wrap gap-1.5 px-2">
                {LANGUAGES?.map((l) => (
                  <button
                    key={`mobile-lang-${l?.code}`}
                    type="button"
                    onClick={() => {
                      pickLang(l?.code);
                      setMobileOpen(false);
                    }}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors duration-100 ${lang === l?.code ? 'bg-[#1B3A5C] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    {l?.flag} {l?.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-3 border-t border-slate-100 mt-3 space-y-2">
              <Link href="/login" className="block btn-secondary text-center text-sm" onClick={() => setMobileOpen(false)}>
                {t('nav.clientPortal')}
              </Link>
              <a
                href={`${homeBase}#contact`}
                className="block btn-primary text-center text-sm"
                onClick={() => setMobileOpen(false)}
              >
                {t('nav.getStarted')}
              </a>
            </div>
          </div>
        </nav>
      </>
    )}
    </>
  );
}